import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AuthState, User, LoginCredentials } from "../../types/auth";
import { AuthService } from "../../services/authService";

// Initialize auth state from localStorage on app startup
const getInitialUser = (): User | null => {
  try {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      return JSON.parse(userStr);
    }
  } catch (error) {
    console.error("Error parsing user from localStorage:", error);
  }
  return null;
};

const initialToken = localStorage.getItem("token");
const initialUser = getInitialUser();

const initialState: AuthState = {
  user: initialUser,
  isAuthenticated: !!(initialToken && initialUser),
  isLoading: false,
  error: null,
  token: initialToken,
  errorMessage: null,
};

export const loginUser = createAsyncThunk(
  "auth/login",
  async (credentials: LoginCredentials, { rejectWithValue, dispatch }) => {
    try {
      const response: any = await AuthService.login(credentials);
      // Call /users-details API after successful login
      if (response?.status == 1) {
        try {
          setTimeout(async () => {
            const userDetails = await AuthService.getUserDetails();
            dispatch(setUser(userDetails.data.user));
            localStorage.setItem("user", JSON.stringify(userDetails.data.user));
          }, 500);
        } catch (userDetailsError) {
          console.warn(
            "Failed to fetch user details after login:",
            userDetailsError
          );
        }

        return response;
      } else {
        return rejectWithValue(response?.message);
      }
    } catch (error) {
      // return rejectWithValue((error as Error).message || "Login failed");
    }
  }
);

export const logoutUser = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      await AuthService.logout();
      return null;
    } catch (error) {
      return rejectWithValue((error as Error).message || "Logout failed");
    }
  }
);

export const getCurrentUser = createAsyncThunk(
  "auth/getCurrentUser",
  async (_, { rejectWithValue }) => {
    try {
      const response = await AuthService.getCurrentUser();
      return response;
    } catch (error) {
      return rejectWithValue(
        (error as Error).message || "Failed to get current user"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.data) {
          state.token = action.payload?.data?.token;
          state.isAuthenticated = true;
          localStorage.setItem("token", action.payload.data.token);
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
      })
      // Logout
      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      })
      // Get Current User
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        localStorage.setItem("user", JSON.stringify(action.payload));
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.isLoading = false;
        // Don't immediately remove token on rejection - let the user try to refresh
        // Only remove if it's a clear authentication error
        if (state.token) {
          // Keep the token but mark as not authenticated
          // This allows the user to stay on the page and try again
          state.isAuthenticated = false;
          state.user = null;
        }
      });
  },
});

export const { clearError, setUser } = authSlice.actions;
export default authSlice.reducer;
